import { Button } from "components/DesignSystem/Button/Button";
import Input from "components/DesignSystem/Input/Input";
import Modal from "components/DesignSystem/Modal/Modal";
import { FormikForm } from "components/FormikForm/FormikForm";
import { GroupSelector } from "components/GroupSelector/GroupSelector";
import { personalTaxUserInviteValidationSchema } from "formValidations/personalTaxUserInviteValidationSchema";
import { ModalControl } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import {
  useCheckValidEmailMobileMutation,
  useInvitePersonalTaxUserMutation,
} from "store/apis/salesCrm";
import { BackendError } from "types/utils/error";

type FormValues = {
  first_name: string;
  last_name: string;
  email: string;
  parent_group_id: string;
};

type PersonalTaxUserInviteProps = {} & ModalControl;

export const PersonalTaxUserInvite = ({
  isOpen,
  close,
}: PersonalTaxUserInviteProps) => {
  const { alertToast, successToast } = useToast();
  const [checkValidEmail] = useCheckValidEmailMobileMutation();
  const [invitePersonalTaxUser] = useInvitePersonalTaxUserMutation();

  const onSubmit = async (values: FormValues) => {
    try {
      const response = await checkValidEmail({
        emails: [values.email],
      }).unwrap();

      if (response?.emails_already_exits.length > 0) {
        alertToast({ message: "Email already in use" });
        return;
      }

      await invitePersonalTaxUser(values).unwrap();
      successToast({ message: "Invite sent" });
      close();
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as Error
      );
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close} modal={false}>
      <Modal.Content asChild useCustomOverlay>
        <FormikForm<FormValues>
          initialValues={{
            first_name: "",
            last_name: "",
            email: "",
            parent_group_id: "",
          }}
          validationSchema={personalTaxUserInviteValidationSchema}
          onSubmit={onSubmit}
        >
          {({ values, isSubmitting, isValid, setFieldValue }) => {
            return (
              <>
                <Modal.Header>
                  <Modal.Title>Invite Personal Tax User</Modal.Title>
                  <Modal.Close />
                </Modal.Header>

                <Modal.Body className="t-flex t-flex-col t-gap-3">
                  <div className="t-flex t-w-full t-gap-3">
                    <Input
                      withForm
                      required
                      name="first_name"
                      label="First name"
                      placeholder="Enter first name"
                    />
                    <Input
                      withForm
                      required
                      name="last_name"
                      label="Last name"
                      placeholder="Enter last name"
                    />
                  </div>
                  <Input
                    withForm
                    required
                    name="email"
                    label="Email"
                    placeholder="Enter email"
                  />
                  <GroupSelector
                    withForm
                    required
                    name="parent_group_id"
                    label="Parent group"
                    placeholder="Select parent group"
                    menuPortalTarget={document.body}
                    isClearable={false}
                  />
                </Modal.Body>
                <Modal.FooterButtonGroup>
                  <Modal.RawClose asChild>
                    <Button size="regular" type="reset" disabled={isSubmitting}>
                      Cancel
                    </Button>
                  </Modal.RawClose>
                  <Button
                    isLoading={isSubmitting}
                    disabled={isSubmitting || !isValid}
                    customType="primary"
                    size="regular"
                    type="submit"
                  >
                    Send Invite
                  </Button>
                </Modal.FooterButtonGroup>
              </>
            );
          }}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
